import { useCallback, useState, useEffect, useMemo } from 'react';
import DocumentHeader from '@/components/DocumentHeader';
import GlobalStyle from '@/styled/styled.default';
import { useTheme, LoadingWarren, Heading, Text } from '@simplywallst/ui-core';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  fetchPage,
  getDefaultResponse,
  PAGE_SIZE,
} from '../../query/fetchPage';
import { SWS_HOST } from '@/constants';
import useReduxStateForStocks from '../../hooks/useReduxStateForStocks';
import { useMetaData } from '../../hooks/useMetaData';
import { flatten } from 'lodash';
import useUser from '@/hooks/useUser';
import track, { useTracking } from 'react-tracking';
import { BreadcrumbWrapper, TextHeader, DividendCTA } from './styled';
import Breadcrumb from '@/components/Breadcrumb';
import ScrollLoader from '@/components/ScrollLoader';
import {
  ROUTE_STOCKS_MARKET,
  ROUTE_STOCKS_MARKET_INDUSTRY,
  ROUTE_INVESTING_IDEA_MARKET,
  ROUTE_STOCKS_HOME,
} from '@/constants/routes';
import DocumentSchema from '@/components/DocumentSchema';
import type {
  SearchResultsPage,
  Corporation,
  ListItem,
  ItemList,
} from 'schema-dts';
import loadable from '@loadable/component';
import { getStocksRouterAction } from '../../utils';
import Header from '../Header';
import { RouterLink } from '@redux-first-router-link';
import { LastUpdated } from '@/components/Header';
import { useStocksRules } from '../../hooks/useStocksRules';
import { DiscoverTabs } from '@/components/Nav/DiscoverTabs';
import { getCloudflareImageUrl } from '@/hooks/useCloudflareImage';
import { getPath } from '@/router/utils';
import { useStocksView } from '../../hooks/useStocksView';
import { Footer } from '@/components/Footer';
import {
  DEFAULT_SORT_BY,
  DEFAULT_INDUSTRY,
  DEFAULT_MARKET,
} from '../../constants';

const TileList = loadable(() => import('@/components/TileList'), {
  resolveComponent: (c) => c.TileList,
});
const StockTable = loadable(() => import('../StockTable'));
const Pagination = loadable(() => import('@/components/Pagination'));

const GuestRegistrationModalOnScroll = loadable(
  () => import('@/components/GuestRegistrationModalOnScroll')
);

const Stocks = () => {
  const theme = useTheme();
  const { trackEvent } = useTracking();
  const { data: user } = useUser();

  const {
    market = DEFAULT_MARKET,
    industry = DEFAULT_INDUSTRY,
    sortBy,
    pageQuery,
    redirect,
    preFetchCompany,
    onAdvancedFilters,
  } = useReduxStateForStocks();

  /**
   * Redirect
   */
  useEffect(() => {
    if (typeof sortBy !== 'undefined') return;
    const routerAction = getStocksRouterAction({
      market,
      industry,
      sortBy: DEFAULT_SORT_BY,
      excludePageQuery: true,
    });
    redirect(routerAction);
  }, [market, industry, sortBy, redirect]);
  /**
   *
   */

  const hasInfiniteScroll = user.loggedIn && pageQuery === 1;

  const [page, setPage] = useState<number>(pageQuery);

  useEffect(() => {
    if (hasInfiniteScroll) return;
    setPage(pageQuery);
  }, [pageQuery, hasInfiniteScroll]);

  const {
    data = { pages: [getDefaultResponse()] },
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetched: isStocksFetched,
  } = useInfiniteQuery({
    queryKey: [
      'stock-page',
      useStocksRules(market, industry, sortBy),
      hasInfiniteScroll ? 1 : page,
    ],
    queryFn: fetchPage,
    staleTime: 3600000,
    initialPageParam: hasInfiniteScroll ? 1 : page,
    getNextPageParam: (lastPage, allPages) => {
      return page < lastPage.meta.totalPages ? allPages.length + 1 : null;
    },
  });

  const { stocksView, handleViewSelect } = useStocksView();

  const handleAdvancedFilterClick = useCallback(() => {
    onAdvancedFilters(market, industry);
    trackEvent({
      action: 'click',
      subject: 'advanced-filter',
    });
  }, [market, industry, onAdvancedFilters, trackEvent]);

  const allCompanies = useMemo(
    () => flatten(data.pages.map((page) => page.companies)),
    [data]
  );

  const routeType =
    industry !== DEFAULT_INDUSTRY
      ? ROUTE_STOCKS_MARKET_INDUSTRY
      : ROUTE_STOCKS_MARKET;

  const meta = useMetaData({
    market,
    industry,
    sortBy,
    page: pageQuery,
    companies: data.pages[0].companies,
    hasInfiniteScroll,
    routeType,
  });

  return (
    <DiscoverTabs route={ROUTE_STOCKS_HOME} market={market}>
      <GlobalStyle background={theme.color.charcoal} />
      <DocumentHeader
        title={meta.paginatedTitle}
        allowCrawlers={meta.allowCrawlers}
        description={meta.description}
      >
        {meta.allowCrawlers && (
          <link
            rel="canonical"
            href={`${meta.url.toLowerCase()}${
              page === 1 ? '' : '?page=' + page
            }`}
          />
        )}
        {meta.hrefLangs && (
          <link
            rel="alternate"
            hrefLang="x-default"
            href={`${SWS_HOST}${getPath({
              type: routeType,
              payload: { market: DEFAULT_MARKET, industry, sortBy },
            })}`}
          />
        )}
        {meta.hrefLangs}
      </DocumentHeader>
      {
        // Show DocumentSchema for logged out users so ssr and hydration is synced
        user.loggedIn === false && RUNTIME_ENV !== 'native' && (
          <DocumentSchema<SearchResultsPage>
            item={{
              '@context': 'https://schema.org',
              '@type': 'SearchResultsPage',
              identifier: `${meta.url}#webpage`,
              url: meta.url,
              mainEntity: [
                {
                  '@type': 'ItemList',
                  name: meta.paginatedTitle,
                  itemListOrder: 'https://schema.org/ItemListOrderDescending',
                  itemListElement: allCompanies.map((n, i) => {
                    return {
                      '@type': 'ListItem',
                      position: i + 1,
                      item: {
                        '@type': 'Corporation',
                        name: n.shortName,
                        legalName: n.legalName,
                        tickerSymbol: n.tickerSymbol,
                        url: `${SWS_HOST}${n.canonicalUrl}`,
                        logo: n.website
                          ? getCloudflareImageUrl({
                              src: `https://images.simplywall.st/asset/logos/${n.website.replace(
                                'https://',
                                ''
                              )}`,
                              width: 50,
                            })
                          : undefined,
                        foundingDate: n.yearFounded,
                        description: n.shortDescription,
                      } as Corporation,
                    } as ListItem;
                  }),
                } as ItemList,
              ],
              isPartOf: {
                '@type': 'WebSite',
                url: SWS_HOST,
                name: 'Simply Wall St',
              },
            }}
          />
        )
      }
      <Header
        market={market}
        industry={industry}
        sortBy={sortBy}
        onAdvancedFilterClick={handleAdvancedFilterClick}
        stocksView={stocksView || 'table'}
        onViewSelect={handleViewSelect}
        totalRecords={
          data.pages[0].meta.totalRecords > PAGE_SIZE
            ? data.pages[0].meta.totalRecords
            : undefined
        }
      >
        {industry !== DEFAULT_INDUSTRY && (
          <BreadcrumbWrapper>
            <Breadcrumb list={meta.breadCrumbs} />
            <span className="text-xs">{` /`}</span>
          </BreadcrumbWrapper>
        )}
        <TextHeader>
          <Heading
            as="h1"
            typography="megaStrong"
            data-cy-id="stock-header-title"
          >
            {meta.paginatedTitle}
          </Heading>
          <LastUpdated />
          <Text typography="xSmall" color="whiteRGBA" opacity={0.5}>
            {meta.blurb}
          </Text>
        </TextHeader>
        {sortBy === 'dividend-yield-high' && (
          <DividendCTA
            display="inline-grid"
            alignContent="center"
            justifyContent="center"
          >
            <span>
              Discover{` `}
              <RouterLink
                to={{
                  type: ROUTE_INVESTING_IDEA_MARKET,
                  payload: {
                    id: '146',
                    name: 'dividend-powerhouses-3-yield',
                    market,
                  },
                }}
                onClick={() => {
                  trackEvent({
                    action: 'click',
                    subject: 'Top Quality Dividend Stocks',
                  });
                }}
              >
                Top Quality {meta.demonymCountry} Dividend Stocks
              </RouterLink>
            </span>
          </DividendCTA>
        )}
      </Header>
      {hasInfiniteScroll ? (
        <>
          <ScrollLoader
            handleLoadRequest={() => {
              if (hasNextPage) {
                setPage(page + 1);
                fetchNextPage();
              }
            }}
          >
            {() =>
              typeof stocksView === 'undefined' || stocksView === 'table' ? (
                <StockTable
                  companies={allCompanies}
                  sortBy={sortBy}
                  industry={industry}
                  market={market}
                  isFetching={isFetching}
                  isFetched={isStocksFetched}
                  onLongHover={preFetchCompany}
                />
              ) : (
                <div className="py-6">
                  <TileList
                    companies={allCompanies}
                    onLongHover={preFetchCompany}
                    isFetching={isFetching}
                    isFetched={isStocksFetched}
                  />
                </div>
              )
            }
          </ScrollLoader>
          {isFetching && isStocksFetched && (
            <div className="pb-6 pt-4">
              <LoadingWarren />
            </div>
          )}
        </>
      ) : (
        <>
          {typeof stocksView === 'undefined' || stocksView === 'table' ? (
            <StockTable
              companies={allCompanies}
              sortBy={sortBy}
              industry={industry}
              market={market}
              isFetching={isFetching}
              isFetched={isStocksFetched}
              onLongHover={preFetchCompany}
            />
          ) : (
            <div className="py-6">
              <TileList
                companies={allCompanies}
                onLongHover={preFetchCompany}
                isFetching={isFetching}
                isFetched={isStocksFetched}
              />
            </div>
          )}
          {data.pages[0].meta.totalPages > 1 && (
            <Pagination
              currentPage={page}
              totalPages={data.pages[0].meta.totalPages}
              actionLink={(page: number) => ({
                type: routeType,
                payload: {
                  market,
                  industry,
                  sortBy,
                  query: { page: page + '' },
                },
              })}
              loading={isFetching}
            />
          )}
        </>
      )}
      {!user.loggedIn && <GuestRegistrationModalOnScroll />}
      {(!hasInfiniteScroll || data.pages[0].meta.totalRecords <= PAGE_SIZE) && (
        <Footer />
      )}
    </DiscoverTabs>
  );
};

export default track(
  {
    type: 'track',
    subject: 'page',
    action: 'landed',
  },
  { dispatchOnMount: true }
)(Stocks) as typeof Stocks;
